import { memo, useEffect } from 'react';
import { isAiConfigAvailableForUser } from 'shared-helpers/aiTools';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { AIGenerateContentButton } from 'components/aiTools';
import { Button, LinkButton, MoreOptions } from 'components/common';
import { Icon } from 'components/common/icons';
import { EditorCourseEvaluation } from 'components/editor/evaluation';
import { editorSocket } from 'components/editor/socket';
import {
  EditorHeaderChapterVersionHistory,
  EditorHeaderCopy,
  EditorHeaderDelete
} from '.';

import type { EditorHeaderStateProps } from './EditorHeader';

type StateProps = Pick<
  EditorHeaderStateProps,
  | 'activeLanguageVersion'
  | 'chapters'
  | 'currentChapter'
  | 'importVisible'
  | 'isDisabled'
  | 'isDiscarding'
  | 'isDraft'
  | 'isSaving'
> & {
  updateTranslationProgress: () => void;
};

interface Props extends StateProps {
  updateTranslationProgress: () => void;
}

const EditorHeaderActions = ({
  activeLanguageVersion,
  chapters,
  currentChapter,
  importVisible,
  isDisabled,
  isDiscarding,
  isDraft,
  isSaving,
  updateTranslationProgress
}: Props) => {
  const {
    messages,
    pageState: { course },
    serverConfig,
    theme,
    user
  } = useReduxState();

  const canUseCourseEvaluation = isAiConfigAvailableForUser({
    requiredAiExtraServerConfig: 'aiExtraCourseEvaluation',
    serverConfig,
    user
  });

  // update translation progress after saving changes
  useEffect(() => {
    // only update if there are language versions
    if (
      !isSaving &&
      course?.languageVersions &&
      Object.keys(course.languageVersions).length > 0
    ) {
      updateTranslationProgress();
    }
  }, [isSaving, updateTranslationProgress, course?.languageVersions]);

  const handleDiscardClick = () =>
    editorSocket.discardChanges({ chapterId: currentChapter.id });

  const handleImportClick = () => editorSocket.toggleImport();

  const handleSaveClick = async () => {
    editorSocket.saveChanges({ chapterId: currentChapter.id });
    // if there are language versions, re-fetch block data on save to keep
    // translation progress and missing translation information up to date
    if (
      course?.languageVersions &&
      Object.keys(course.languageVersions).length > 0
    ) {
      editorSocket.fetchInitialData({ chapterId: currentChapter.id });
    }
  };

  return (
    <div className="actions">
      <Button
        category="light"
        disabled={isDisabled || isSaving || !isDraft}
        icon="trash-alt"
        loading={isDiscarding}
        onClick={handleDiscardClick}
        size="header"
        style={{ marginRight: 8 }}
        text={messages.commonDiscard}
        tooltip={messages.commonDiscardChanges}
      />

      <Button
        category="success"
        disabled={isDisabled || isDiscarding || !isDraft}
        icon="check"
        loading={isSaving}
        onClick={handleSaveClick}
        size="header"
        text={messages.commonSave}
        tooltip={messages.commonSaveChanges}
      />

      <div className="separator" />

      <LinkButton
        href={`/courses/${course.slug}/${currentChapter.slug}?preview=${
          activeLanguageVersion || course.language
        }`}
        icon="book"
        size="header"
        tooltip={messages.commonPreview}
      />

      {!activeLanguageVersion && (
        <div className="secondary">
          <div className="separator separator-wide" />

          <Button
            category="lighter"
            icon="file-import"
            onClick={handleImportClick}
            size="header"
            style={{ marginRight: 7 }}
            text={
              <span>
                <Icon
                  icon={importVisible ? 'chevron-up' : 'chevron-down'}
                  style={{ fontSize: '1.2rem' }}
                />
              </span>
            }
            tooltip={messages.editorImportTitle}
          />

          <AIGenerateContentButton
            buttonProps={{
              customBackgroundColor: theme.successLightColor,
              customBackgroundColorHover: `color-mix(in srgb, ${theme.successLightColor}, black 5%)`,
              customColor: theme.successLightTextColor
            }}
            category="light"
            size="header"
            style={{ marginRight: 7 }}
          />

          {canUseCourseEvaluation ? (
            <div className="course-evaluation">
              <EditorCourseEvaluation />
            </div>
          ) : null}

          <div className="separator" />

          <MoreOptions category="lighter" styleContentButtons={false}>
            <EditorHeaderCopy currentChapter={currentChapter} />
            <EditorHeaderChapterVersionHistory />
            <EditorHeaderDelete
              chapters={chapters}
              currentChapter={currentChapter}
            />
          </MoreOptions>
        </div>
      )}

      <style jsx>{`
        .actions {
          position: relative;
        }
        .separator {
          background-color: #ddd;
          display: inline-block;
          height: 30px;
          margin: -10px 10px;
          width: 1px;
        }
        .separator-wide {
          display: none;
        }
        .secondary {
          display: block;
          margin-top: 10px;
        }
        .course-evaluation {
          display: inline-block;
        }
        @media (min-width: ${breakpoints.small}px) {
          .separator-wide {
            display: inline-block;
          }
          .secondary {
            display: inline-block;
            margin-top: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default memo(EditorHeaderActions);
